import { Money } from '@shared/lib/money/money'
import type { MonetaryValue } from '@shared/lib/money/money'
import type { Currency, SaleStatus, SubscriptionStatus } from '@shared/contracts/enums'
import type { SaleListItem } from './sale.contract'
import {
  groupSalesByFunnel,
  receivedAmountOf,
  summarizeJourney,
  summarizeSaleAmountImpact,
} from './sale-grouping'
import type { SaleAmountImpact, SaleJourney } from './sale-grouping'

export interface RenewalRef {
  subscriptionId: string
  cycleN: number
}

export function parseSubscriptionSaleId(id: string): RenewalRef | null {
  const prefix = 'invoice:'
  if (!id.startsWith(prefix)) {
    return null
  }
  const rest = id.slice(prefix.length)
  const separator = rest.lastIndexOf(':')
  if (separator <= 0 || separator === rest.length - 1) {
    return null
  }
  const subscriptionId = rest.slice(0, separator)
  const cycleN = Number(rest.slice(separator + 1))
  if (!Number.isInteger(cycleN) || cycleN < 1) {
    return null
  }
  return { subscriptionId, cycleN }
}

function subscriptionRefFrom(sale: SaleListItem): RenewalRef | null {
  if (sale.subscriptionId) {
    return {
      subscriptionId: sale.subscriptionId,
      cycleN: sale.subscriptionCycleN ?? 1,
    }
  }
  return parseSubscriptionSaleId(sale.id)
}

function chargeIsRenewal(sale: SaleListItem): boolean {
  if (sale.isRenewal !== undefined) {
    return sale.isRenewal
  }
  return parseSubscriptionSaleId(sale.id) !== null
}

export function isSubscriptionSale(sale: SaleListItem): boolean {
  return subscriptionRefFrom(sale) !== null
}

export type SubscriptionHealth = 'active' | 'overdue' | 'ended'

export interface SubscriptionCharge {
  sale: SaleListItem
  cycleN: number
  isRenewal: boolean
  funnelChildren: SaleListItem[]
}

export function individualSubscriptionCharges(
  charges: readonly SubscriptionCharge[],
): SubscriptionCharge[] {
  return charges.flatMap((charge) => [
    { ...charge, funnelChildren: [] },
    ...charge.funnelChildren.map((sale) => ({
      sale,
      cycleN: sale.subscriptionCycleN ?? 1,
      isRenewal: chargeIsRenewal(sale),
      funnelChildren: [],
    })),
  ])
}

export interface SubscriptionGroup {
  subscriptionId: string
  buyerName: string
  productName: string
  charges: SubscriptionCharge[]
  firstAt: string
  lastAt: string
  chargeCount: number
  renewalCount: number
  accumulated: MonetaryValue
  originalAccumulated: MonetaryValue
  affectedAccumulated: MonetaryValue
  hasFinancialImpact: boolean
  health: SubscriptionHealth
  latestStatus: SaleStatus
  subscriptionStatus?: SubscriptionStatus
  hasConversion: boolean
  companionConvertedElsewhere: boolean
  affiliateSale: boolean
  originSaleId?: string
}

function healthFrom(
  latest: SaleStatus,
  subscriptionStatus?: SubscriptionStatus,
): SubscriptionHealth {
  if (subscriptionStatus === 'ACTIVE' || subscriptionStatus === 'TRIALING') {
    return 'active'
  }
  if (subscriptionStatus === 'PAST_DUE') {
    return 'overdue'
  }
  if (subscriptionStatus === 'CANCELED') {
    return 'ended'
  }
  if (latest === 'FAILED' || latest === 'PENDING') {
    return 'overdue'
  }
  if (latest === 'REFUNDED' || latest === 'CHARGED_BACK') {
    return 'ended'
  }
  return 'active'
}

function isCheckoutCompanion(sale: SaleListItem, saleById: Map<string, SaleListItem>): boolean {
  if (sale.originSaleId === undefined || chargeIsRenewal(sale)) {
    return false
  }
  const parent = saleById.get(sale.originSaleId)
  return parent !== undefined && subscriptionRefFrom(parent) !== null && !chargeIsRenewal(parent)
}

export function summarizeSubscriptionCharge(charge: SubscriptionCharge): SaleAmountImpact {
  if (charge.funnelChildren.length === 0) {
    return summarizeSaleAmountImpact(charge.sale)
  }
  const journey = summarizeJourney({ sale: charge.sale, children: charge.funnelChildren })
  return {
    active: journey.total,
    original: journey.originalTotal,
    affected: journey.affectedTotal,
    hasImpact: journey.hasFinancialImpact,
  }
}

export function subscriptionChargeExtraTags(charge: SubscriptionCharge): string[] {
  const tags: string[] = []
  const embedded = charge.sale.funnelItemCount ?? 0
  if (embedded > 0) {
    tags.push(embedded === 1 ? '+1 item na cobrança' : `+${embedded} itens na cobrança`)
  }
  if (charge.funnelChildren.length > 0) {
    tags.push('Etapa do funil')
  }
  return tags
}

export function subscriptionChargeExtrasLabel(charge: SubscriptionCharge): string | null {
  const tags = subscriptionChargeExtraTags(charge)
  return tags.length > 0 ? tags.join(' · ') : null
}

export function groupSalesBySubscription(sales: SaleListItem[]): SubscriptionGroup[] {
  const saleById = new Map(sales.map((sale) => [sale.id, sale]))
  const companionsByParent = new Map<string, SaleListItem[]>()
  const companionSubscriptionIds = new Set<string>()
  const companionBySubscriptionId = new Map<string, SaleListItem>()

  for (const sale of sales) {
    if (!isCheckoutCompanion(sale, saleById)) {
      continue
    }
    const list = companionsByParent.get(sale.originSaleId!) ?? []
    list.push(sale)
    companionsByParent.set(sale.originSaleId!, list)
    const ref = subscriptionRefFrom(sale)
    if (ref) {
      companionSubscriptionIds.add(ref.subscriptionId)
      companionBySubscriptionId.set(ref.subscriptionId, sale)
    }
  }

  const buckets = new Map<string, SubscriptionCharge[]>()
  for (const sale of sales) {
    if (isCheckoutCompanion(sale, saleById)) {
      continue
    }
    const ref = subscriptionRefFrom(sale)
    if (!ref) {
      continue
    }
    const list = buckets.get(ref.subscriptionId) ?? []
    list.push({
      sale,
      cycleN: ref.cycleN,
      isRenewal: chargeIsRenewal(sale),
      funnelChildren: companionsByParent.get(sale.id) ?? [],
    })
    buckets.set(ref.subscriptionId, list)
  }

  const groups: SubscriptionGroup[] = []
  for (const [subscriptionId, charges] of buckets) {
    charges.sort((left, right) => {
      if (left.cycleN !== right.cycleN) {
        return left.cycleN - right.cycleN
      }
      if (left.isRenewal !== right.isRenewal) {
        return left.isRenewal ? 1 : -1
      }
      return left.sale.createdAt.localeCompare(right.sale.createdAt)
    })
    const first = charges[0]!
    const last = charges[charges.length - 1]!
    const subscriptionStatus = charges.find((charge) => charge.sale.subscriptionStatus)?.sale
      .subscriptionStatus
    const hasConversion = charges.some(
      (charge) => !charge.isRenewal && charge.sale.status === 'PAID',
    )

    const companion = companionBySubscriptionId.get(subscriptionId)
    const companionConvertedElsewhere = companionSubscriptionIds.has(subscriptionId)
    const charged = charges.filter((charge) =>
      ['PAID', 'REFUND_REQUESTED', 'REFUNDED', 'CHARGED_BACK'].includes(charge.sale.status),
    )
    const currency: Currency = receivedAmountOf((charged[0] ?? first).sale).currency
    let accumulated = new Money(0, currency)
    let originalAccumulated = new Money(0, currency)
    for (const charge of charged) {
      const impact = summarizeSubscriptionCharge(charge)
      if (impact.original.currency === currency) {
        originalAccumulated = originalAccumulated.add(Money.fromMonetaryValue(impact.original))
      }
      if (impact.active.currency === currency) {
        accumulated = accumulated.add(Money.fromMonetaryValue(impact.active))
      }
    }
    const firstAt = charges.reduce(
      (min, charge) => (charge.sale.createdAt < min ? charge.sale.createdAt : min),
      first.sale.createdAt,
    )
    const derivedLastAt = charges.reduce(
      (max, charge) => (charge.sale.createdAt > max ? charge.sale.createdAt : max),
      first.sale.createdAt,
    )

    const summary = charges
      .map((charge) => charge.sale)
      .find((sale) => sale.subscriptionChargeCount !== undefined)
    const hasBackendSummary = summary !== undefined
    const accumulatedValue = summary?.subscriptionAccumulated
      ? Money.fromMonetaryValue(summary.subscriptionAccumulated)
      : accumulated
    const originalValue = hasBackendSummary ? accumulatedValue : originalAccumulated
    const affectedValue = originalValue.subtract(accumulatedValue)
    const lastAt = summary?.subscriptionLastActivityAt ?? derivedLastAt
    const latestStatus = summary?.subscriptionLastChargeStatus ?? last.sale.status

    groups.push({
      subscriptionId,
      buyerName: first.sale.buyerName,
      productName: first.sale.productName,
      charges,
      firstAt,
      lastAt,
      chargeCount:
        summary?.subscriptionChargeCount ?? individualSubscriptionCharges(charges).length,
      renewalCount:
        summary?.subscriptionRenewalCount ?? charges.filter((charge) => charge.isRenewal).length,
      accumulated: accumulatedValue.toMonetaryValue(),
      originalAccumulated: originalValue.toMonetaryValue(),
      affectedAccumulated: affectedValue.toMonetaryValue(),
      hasFinancialImpact: !affectedValue.isZero(),
      health: healthFrom(latestStatus, subscriptionStatus),
      latestStatus,
      ...(subscriptionStatus !== undefined ? { subscriptionStatus } : {}),
      hasConversion,
      companionConvertedElsewhere,
      affiliateSale:
        charges.some((charge) => charge.sale.affiliateSale === true) ||
        companion?.affiliateSale === true,
      ...((charges.find((charge) => charge.sale.originSaleId)?.sale.originSaleId ??
      companion?.originSaleId)
        ? {
            originSaleId:
              charges.find((charge) => charge.sale.originSaleId)?.sale.originSaleId ??
              companion?.originSaleId,
          }
        : {}),
    })
  }
  return groups
}

export type SalesRow =
  | { type: 'subscription'; sortAt: string; group: SubscriptionGroup }
  | { type: 'journey'; sortAt: string; journey: SaleJourney }

export function buildSalesRows(sales: SaleListItem[]): SalesRow[] {
  const saleById = new Map(sales.map((sale) => [sale.id, sale]))
  const subscriptions = groupSalesBySubscription(sales)
  const journeys = groupSalesByFunnel(
    sales.filter((sale) => !isSubscriptionSale(sale) && !isCheckoutCompanion(sale, saleById)),
  )

  const rows: SalesRow[] = [
    ...subscriptions.map(
      (group): SalesRow => ({ type: 'subscription', sortAt: group.lastAt, group }),
    ),
    ...journeys.map(
      (journey): SalesRow => ({ type: 'journey', sortAt: journey.sale.createdAt, journey }),
    ),
  ]

  rows.sort((left, right) => {
    if (left.sortAt === right.sortAt) {
      return 0
    }
    return left.sortAt < right.sortAt ? 1 : -1
  })
  return rows
}

export function subscriptionChargeLabel(
  charge: SubscriptionCharge,
  _group: SubscriptionGroup,
): string {
  if (!charge.isRenewal) {
    return 'Primeira cobrança'
  }
  return `Renovação · ciclo ${displayRenewalCycle(charge)}`
}

export function displayRenewalCycle(charge: SubscriptionCharge): number {
  return charge.cycleN
}

export type SalesView = 'all' | 'new' | 'renewals'

export function filterRowsByView(rows: SalesRow[], view: SalesView): SalesRow[] {
  if (view === 'new') {
    return rows.filter((row) => row.type === 'journey' || row.group.hasConversion)
  }
  if (view === 'renewals') {
    return rows.filter((row) => row.type === 'subscription')
  }
  return rows
}

export interface SalesKpis {
  newCustomers: number
  renewalCount: number
  recurringRevenue: MonetaryValue
  totalPaid: MonetaryValue
}

function addPaid(total: Money, sale: SaleListItem, currency: Currency): Money {
  if (sale.status !== 'PAID') {
    return total
  }
  const received = receivedAmountOf(sale)
  if (received.currency !== currency) {
    return total
  }
  return total.add(Money.fromMonetaryValue(received))
}

export function salesCurrenciesOf(sales: SaleListItem[]): Currency[] {
  const currencies = new Set<Currency>()
  for (const sale of sales) {
    currencies.add(receivedAmountOf(sale).currency)
  }
  return Array.from(currencies).sort((left, right) => left.localeCompare(right))
}

export function filterSalesByCurrency(sales: SaleListItem[], currency: Currency): SaleListItem[] {
  return sales.filter((sale) => receivedAmountOf(sale).currency === currency)
}

export function defaultSalesCurrencyOf(sales: SaleListItem[]): Currency {
  const currencies = salesCurrenciesOf(sales)
  return currencies.includes('BRL') ? 'BRL' : (currencies[0] ?? 'BRL')
}

function defaultKpiCurrencyOf(rows: SalesRow[]): Currency {
  const currencies = new Set<Currency>()
  for (const row of rows) {
    currencies.add(
      row.type === 'journey'
        ? receivedAmountOf(row.journey.sale).currency
        : row.group.accumulated.currency,
    )
  }
  const ordered = Array.from(currencies).sort((left, right) => left.localeCompare(right))
  return ordered.includes('BRL') ? 'BRL' : (ordered[0] ?? 'BRL')
}

export function computeSalesKpis(
  rows: SalesRow[],
  currency: Currency = defaultKpiCurrencyOf(rows),
): SalesKpis {
  let newCustomers = 0
  let renewalCount = 0
  let recurring = new Money(0, currency)
  let total = new Money(0, currency)

  const addToTotal = (sale: SaleListItem, recurringSale: boolean) => {
    total = addPaid(total, sale, currency)
    if (recurringSale) {
      recurring = addPaid(recurring, sale, currency)
    }
  }

  for (const row of rows) {
    if (row.type === 'journey') {
      if (row.journey.sale.status === 'PAID' && row.journey.sale.originSaleId === undefined) {
        newCustomers += 1
      }
      addToTotal(row.journey.sale, false)
      for (const child of row.journey.children) {
        addToTotal(child, false)
      }
      continue
    }

    if (row.group.hasConversion) {
      newCustomers += 1
    }
    for (const charge of row.group.charges) {
      const isPaidRenewal = charge.isRenewal && charge.sale.status === 'PAID'
      addToTotal(charge.sale, isPaidRenewal)
      for (const child of charge.funnelChildren) {
        addToTotal(child, false)
      }
      if (isPaidRenewal) {
        renewalCount += 1
      }
    }
  }

  return {
    newCustomers,
    renewalCount,
    recurringRevenue: recurring.toMonetaryValue(),
    totalPaid: total.toMonetaryValue(),
  }
}
